import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTablePageEvent,
  DataTableRowClickEvent,
  DataTableSelectionMultipleChangeEvent,
} from 'primereact/datatable';
import { Tag } from 'primereact/tag';
import { toast } from 'react-toastify';
import { AccountingDepositDetailsEntity } from '../../../../../api/types/accounting/accounting-deposit-details-entity';
import { WarningDialog } from '../../../../../component/ui/warning-dialog/warning-dialog';
import { formatDate } from '../../../../../utils/date-format';
import { DepositDetailsDialog } from './components/deposit-details-dialog';
import { deleteAccountingDepositOperation } from './operations/delete-accounting-deposit-operation';
import {
  exportDepositsToExcelOperation,
  MAX_DEPOSITS_EXPORT_BATCH,
} from './operations/export-deposits-to-excel-operation';
import { getAccountingDepositsListOperation } from './operations/get-accounting-deposits-list-operation';

export const DepositsTab = () => {
  const [data, setData] = useState<AccountingDepositDetailsEntity[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(false);

  const [selectedDeposit, setSelectedDeposit] =
    useState<AccountingDepositDetailsEntity | null>(null);

  const [depositToDelete, setDepositToDelete] =
    useState<AccountingDepositDetailsEntity | null>(null);
  const [deletingDeposit, setDeletingDeposit] = useState<boolean>(false);

  // Multi-select for the bulk Excel export. PrimeReact uses `dataKey` to
  // preserve selection across lazy pages, so this array can hold rows that
  // aren't on the current page. Capped client-side at
  // `MAX_DEPOSITS_EXPORT_BATCH` to keep each export request bounded.
  const [selectedDeposits, setSelectedDeposits] = useState<
    AccountingDepositDetailsEntity[]
  >([]);
  const [exportingExcel, setExportingExcel] = useState<boolean>(false);

  const fetchDeposits = async (nextOffset: number, nextLimit: number) => {
    await getAccountingDepositsListOperation(
      nextOffset,
      nextLimit,
      setData,
      setTotal,
      setLoading,
    );
  };

  useEffect(() => {
    fetchDeposits(offset, limit);
  }, []);

  const onPageChange = async (event: DataTablePageEvent) => {
    const newOffset = event.first;
    const newLimit = event.rows;
    setOffset(newOffset);
    setLimit(newLimit);
    await fetchDeposits(newOffset, newLimit);
  };

  const onRowClick = (event: DataTableRowClickEvent) => {
    const row = event.data as AccountingDepositDetailsEntity;
    if (row) setSelectedDeposit(row);
  };

  const onDeleteClick = (
    e: React.MouseEvent,
    row: AccountingDepositDetailsEntity,
  ) => {
    e.stopPropagation();
    setDepositToDelete(row);
  };

  const onConfirmDelete = async () => {
    if (!depositToDelete) return;
    await deleteAccountingDepositOperation(
      depositToDelete.id,
      setDeletingDeposit,
      async () => {
        setDepositToDelete(null);
        await fetchDeposits(offset, limit);
      },
    );
  };

  // PrimeReact hands us the full *new* selection on every change (across all
  // lazy pages). We enforce the export cap here by rejecting the change and
  // surfacing a Hebrew toast so the user understands why nothing happened.
  const onSelectionChange = (
    event: DataTableSelectionMultipleChangeEvent<
      AccountingDepositDetailsEntity[]
    >,
  ) => {
    const next = (event.value ?? []) as AccountingDepositDetailsEntity[];
    if (next.length > MAX_DEPOSITS_EXPORT_BATCH) {
      toast.warn(
        `ניתן לבחור עד ${MAX_DEPOSITS_EXPORT_BATCH} הפקדות לייצוא בלחיצה אחת`,
      );
      return;
    }
    setSelectedDeposits(next);
  };

  const onExportSelectedToExcel = async () => {
    await exportDepositsToExcelOperation(selectedDeposits, setExportingExcel);
  };

  const onClearSelection = () => {
    setSelectedDeposits([]);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '12px',
        direction: 'rtl',
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{ fontSize: '14px', color: '#4b5563', textAlign: 'right' }}
          >
            סה״כ הפקדות: {total.toLocaleString()}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ fontSize: '13px', color: '#475569' }}>
              נבחרו {selectedDeposits.length} מתוך {MAX_DEPOSITS_EXPORT_BATCH}{' '}
              הפקדות
            </div>

            {selectedDeposits.length > 0 && (
              <Button
                label="נקה בחירה"
                icon="pi pi-times"
                className="p-button-text p-button-secondary"
                onClick={onClearSelection}
                disabled={exportingExcel}
                style={{ height: '32px' }}
              />
            )}

            <Button
              label="ייצוא לאקסל"
              icon="pi pi-file-excel"
              tooltip={
                selectedDeposits.length === 0
                  ? 'יש לבחור הפקדות לייצוא'
                  : `ייצוא ${selectedDeposits.length} הפקדות לאקסל`
              }
              tooltipOptions={{ position: 'top' }}
              onClick={onExportSelectedToExcel}
              loading={exportingExcel}
              disabled={selectedDeposits.length === 0 || exportingExcel}
              style={{
                backgroundColor: '#10b981',
                borderColor: '#10b981',
                height: '32px',
              }}
            />
          </div>
        </div>

        <DataTable
          value={data}
          rowHover
          loading={loading}
          paginator
          rows={limit}
          first={offset}
          totalRecords={total}
          onPage={onPageChange}
          lazy={true}
          rowsPerPageOptions={[10, 20, 50, 100]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו הפקדות</div>
          }
          dataKey="id"
          tableStyle={{ direction: 'rtl' }}
          onRowClick={onRowClick}
          rowClassName={() => 'deposit-row-clickable'}
          // "checkbox" mode (vs. "multiple") restricts selection toggling to
          // the dedicated checkbox column, so clicking elsewhere on a row
          // still opens the details dialog via `onRowClick` instead of also
          // toggling the row's selection state.
          selectionMode="checkbox"
          selection={selectedDeposits}
          onSelectionChange={onSelectionChange}
        >
          <Column
            selectionMode="multiple"
            headerStyle={{ width: '3rem' }}
            style={{ width: '3rem', textAlign: 'center' }}
          />
          <Column
            field="depositSerialNumber"
            header="מספר הפקדה"
            style={{ textAlign: 'right', width: '120px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDepositDetailsEntity) =>
              typeof row.depositSerialNumber === 'number'
                ? row.depositSerialNumber
                : 'חסר'
            }
          />
          <Column
            field="depositDate"
            header="תאריך הפקדה"
            style={{ textAlign: 'right', width: '140px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDepositDetailsEntity) =>
              row.depositDate ? formatDate(row.depositDate as Date) : 'חסר'
            }
          />
          <Column
            field="totalAmount"
            header="סכום כולל"
            style={{ textAlign: 'right', width: '140px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDepositDetailsEntity) =>
              typeof row.totalAmount === 'number'
                ? row.totalAmount.toFixed(2)
                : 'חסר'
            }
          />
          <Column
            field="isLocked"
            header="סטטוס"
            style={{ textAlign: 'right', width: '110px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDepositDetailsEntity) => (
              <Tag
                value={row.isLocked ? 'נעולה' : 'פתוחה'}
                severity={row.isLocked ? 'success' : 'warning'}
              />
            )}
          />
          <Column
            field="bankAccount.bankName"
            header="חשבון בנק"
            style={{ textAlign: 'right', width: '120px' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="itemsCount"
            header="כמות פריטים"
            style={{ textAlign: 'right', width: '120px' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="cashCount"
            header="מזומן"
            style={{ textAlign: 'right', width: '100px' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="checkCount"
            header="המחאות"
            style={{ textAlign: 'right', width: '100px' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="notes"
            header="הערות"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDepositDetailsEntity) =>
              row.notes?.trim() ? row.notes : '—'
            }
          />
          <Column
            field="createdByName"
            header="נוצר על ידי"
            style={{ textAlign: 'right', width: '160px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDepositDetailsEntity) =>
              row.createdByName?.trim() ? row.createdByName : 'חסר'
            }
          />
          <Column
            field="createdAt"
            header="תאריך יצירה"
            style={{ textAlign: 'right', width: '140px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: AccountingDepositDetailsEntity) =>
              row.createdAt ? formatDate(row.createdAt as Date) : 'חסר'
            }
          />
          {/*<Column
            header=""
            style={{ textAlign: 'center', width: '70px' }}
            headerStyle={{ textAlign: 'center' }}
            body={(row: AccountingDepositDetailsEntity) => (
              <Button
                icon="pi pi-trash"
                text
                severity="danger"
                tooltip="מחיקת הפקדה"
                tooltipOptions={{ position: 'top' }}
                onClick={(e) => onDeleteClick(e, row)}
              />
            )}
          />*/}
        </DataTable>
      </div>

      <DepositDetailsDialog
        visible={selectedDeposit !== null}
        deposit={selectedDeposit}
        onHide={() => setSelectedDeposit(null)}
        onDepositChanged={() => fetchDeposits(offset, limit)}
      />

      <WarningDialog
        header="מחיקת הפקדה"
        isVisible={depositToDelete !== null}
        onHide={() => {
          if (!deletingDeposit) setDepositToDelete(null);
        }}
        addIcon
        content={
          depositToDelete
            ? `האם אתה בטוח שברצונך למחוק את הפקדה #${depositToDelete.id}?`
            : 'האם אתה בטוח שברצונך למחוק את ההפקדה?'
        }
        dangerousContent={
          depositToDelete
            ? `סכום: ${
                typeof depositToDelete.totalAmount === 'number'
                  ? depositToDelete.totalAmount.toFixed(2)
                  : 'חסר'
              } ש״ח | תאריך: ${
                depositToDelete.depositDate
                  ? formatDate(depositToDelete.depositDate as Date)
                  : 'חסר'
              }`
            : undefined
        }
        notInformation="לא ניתן לבטל פעולה זו. תשלומי ההפקדה יחזרו לרשימת הממתינים להפקדה."
        requiredTypeingToApproved="delete"
        confirmLabel="מחק הפקדה"
        cancelLabel="ביטול"
        confirmLoading={deletingDeposit}
        onConfirm={onConfirmDelete}
      />

      <style>{`
        .deposit-row-clickable {
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};
